import React, { useEffect, useState } from 'react'
import './TemplateUploadDialog.css'

const STORAGE_KEY = 'girasoles.v2.advancedRefine'

export interface AdvancedRefineOverrides {
  enabled: boolean
  /** Shell thickness in mm — bench_g0.4_hull preset uses 12 mm. */
  thickness_mm: number
  /** Top-surface KNN smoothing iterations (Laplacian-style). */
  top_smooth_iters: number
  /** Rim-band Taubin smoothing iterations. */
  rim_taubin_iters: number
  /** Interior grid step in mm (smaller = higher resolution). */
  grid_step_mm: number
  /** Rim fillet radius in mm (0 disables). */
  rim_fillet_mm: number
}

const DEFAULTS: AdvancedRefineOverrides = {
  enabled: false,
  thickness_mm: 12.0,
  top_smooth_iters: 112,
  rim_taubin_iters: 12,
  grid_step_mm: 0.4,
  rim_fillet_mm: 2.0,
}

/** Read persisted advanced refine overrides (returns null when disabled). */
export function loadAdvancedRefine(): AdvancedRefineOverrides | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<AdvancedRefineOverrides>
    if (!v.enabled) return null
    return { ...DEFAULTS, ...v, enabled: true }
  } catch {
    return null
  }
}

export interface AdvancedOptionsDialogProps {
  isOpen: boolean
  onClose: () => void
}

export const AdvancedOptionsDialog: React.FC<AdvancedOptionsDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [opts, setOpts] = useState<AdvancedRefineOverrides>(DEFAULTS)

  useEffect(() => {
    if (!isOpen) return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) setOpts({ ...DEFAULTS, ...JSON.parse(raw) })
      else setOpts(DEFAULTS)
    } catch {
      setOpts(DEFAULTS)
    }
  }, [isOpen])

  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(opts))
    } catch {
      /* ignore */
    }
    onClose()
  }

  const reset = () => setOpts(DEFAULTS)

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: '95vw' }}
      >
        <div className="modal-header">
          <h3>Advanced Insole Options</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 13, color: '#475569', marginTop: 0 }}>
            Override the V2 generation refine parameters. Settings persist
            in this browser. Defaults below match the
            {' '}<code>bench_g0.4_hull</code>{' '}
            QA preset (12 mm thick, Taubin/Laplacian smoothed).
          </p>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={opts.enabled}
                onChange={(e) => setOpts({ ...opts, enabled: e.target.checked })}
              />{' '}
              Enable advanced overrides on next V2 generation
            </label>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Shell thickness (mm)</label>
              <input
                type="number"
                min={0.5}
                max={20}
                step={0.5}
                value={opts.thickness_mm}
                onChange={(e) =>
                  setOpts({ ...opts, thickness_mm: Number(e.target.value) })
                }
              />
            </div>
            <div className="form-group">
              <label>Grid step (mm)</label>
              <input
                type="number"
                min={0.2}
                max={2}
                step={0.1}
                value={opts.grid_step_mm}
                onChange={(e) =>
                  setOpts({ ...opts, grid_step_mm: Number(e.target.value) })
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Top surface smoothing (Laplacian iters)</label>
              <input
                type="number"
                min={0}
                max={500}
                step={1}
                value={opts.top_smooth_iters}
                onChange={(e) =>
                  setOpts({ ...opts, top_smooth_iters: Number(e.target.value) })
                }
              />
            </div>
            <div className="form-group">
              <label>Rim Taubin iters</label>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={opts.rim_taubin_iters}
                onChange={(e) =>
                  setOpts({ ...opts, rim_taubin_iters: Number(e.target.value) })
                }
              />
            </div>
            <div className="form-group">
              <label>Rim fillet (mm)</label>
              <input
                type="number"
                min={0}
                max={10}
                step={0.5}
                value={opts.rim_fillet_mm}
                onChange={(e) =>
                  setOpts({ ...opts, rim_fillet_mm: Number(e.target.value) })
                }
              />
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn-cancel" onClick={reset}>Reset to bench preset</button>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  )
}
